import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Alert, Badge, Button, Card, Field, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { useStepUp } from '../../lib/step-up';
import { KEYS, type OrganizationFields, suggestSubdomain, type TenantSummary, tenantUrl } from './data';
import { OrganizationFieldset } from './OrganizationFieldset';

const GIB = 1024 ** 3;

/** Tenants de la plataforma (solo desde el tenant operador). Crear, editar y desactivar exigen 2FA reciente. */
export function TenantsPage() {
  const qc = useQueryClient();
  const { run, prompt } = useStepUp();
  const tenants = useQuery({ queryKey: KEYS.tenants, queryFn: () => api<TenantSummary[]>('/api/plataforma/tenants') });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<TenantSummary | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'warning'; text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Ejecuta una acción sensible (con re-autenticación si hace falta) y recarga la lista. */
  async function act(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await run(fn);
      await qc.invalidateQueries({ queryKey: KEYS.tenants });
    } catch (err) {
      if (!(err instanceof Error && err.message === 'cancelado')) setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const setActive = (tn: TenantSummary, active: boolean) =>
    act(async () => {
      await api(`/api/plataforma/tenants/${tn.id}`, { method: 'PATCH', json: { active } });
      setConfirming(null);
    });

  return (
    <div className="space-y-4">
      {prompt}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-tinta">{t('plataforma.tenants_titulo')}</h1>
          <p className="max-w-2xl text-sm text-slate-600">{t('plataforma.tenants_descripcion')}</p>
        </div>
        {!creating && (
          <Button
            onClick={() => {
              setNotice(null);
              setCreating(true);
            }}
          >
            {t('plataforma.nuevo_tenant')}
          </Button>
        )}
      </div>
      {(error ?? tenants.error) && <Alert>{error ?? errorMessage(tenants.error)}</Alert>}
      {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}

      {creating && (
        <NewTenantForm
          busy={busy}
          onCancel={() => setCreating(false)}
          onSubmit={(body) =>
            act(async () => {
              const r = await api<{ id: string; invitationQueued: boolean }>('/api/plataforma/tenants', { json: body });
              setCreating(false);
              setNotice(
                r.invitationQueued
                  ? { tone: 'success', text: t('plataforma.tenant_creado', { correo: body.admin.email }) }
                  : { tone: 'warning', text: t('plataforma.tenant_creado_sin_correo', { url: tenantUrl(body.subdomain) }) },
              );
            })
          }
        />
      )}

      {confirming && (
        <Alert tone="warning">
          <p>{t('plataforma.desactivar_confirmar', { nombre: confirming.name })}</p>
          <div className="mt-2 flex gap-2">
            <Button variant="danger" disabled={busy} onClick={() => void setActive(confirming, false)}>
              {t('plataforma.si_desactivar')}
            </Button>
            <Button variant="secondary" onClick={() => setConfirming(null)}>
              {t('comun.cancelar')}
            </Button>
          </div>
        </Alert>
      )}

      {!tenants.data ? (
        !tenants.error && <Spinner label={t('comun.cargando')} />
      ) : (
        <ul className="space-y-3" aria-label={t('plataforma.tenants_titulo')}>
          {tenants.data.map((tn) => (
            <li key={tn.id}>
              <Card>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="flex flex-wrap items-center gap-2 font-medium text-slate-900">
                      {tn.name}
                      {tn.isPlatform && <Badge tone="blue">{t('plataforma.tenant_operador')}</Badge>}
                      <Badge tone={tn.active ? 'slate' : 'red'}>{tn.active ? t('plataforma.tenant_activo') : t('plataforma.tenant_inactivo')}</Badge>
                    </p>
                    <p className="font-mono text-xs text-slate-500">{tenantUrl(tn.subdomain)}</p>
                    <p className="text-xs text-slate-500">
                      {t('plataforma.tenant_resumen', { orgs: tn.organizations, users: tn.users, gb: Math.round(tn.storageQuotaBytes / GIB) })}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" className="px-3 py-1" aria-label={`${t('plataforma.editar')} ${tn.name}`} onClick={() => setEditing(editing === tn.id ? null : tn.id)}>
                      {t('plataforma.editar')}
                    </Button>
                    {tn.isPlatform ? null : tn.active ? (
                      <Button variant="secondary" className="px-3 py-1" aria-label={`${t('plataforma.desactivar')} ${tn.name}`} disabled={busy} onClick={() => setConfirming(tn)}>
                        {t('plataforma.desactivar')}
                      </Button>
                    ) : (
                      <Button variant="secondary" className="px-3 py-1" aria-label={`${t('plataforma.activar')} ${tn.name}`} disabled={busy} onClick={() => void setActive(tn, true)}>
                        {t('plataforma.activar')}
                      </Button>
                    )}
                  </div>
                </div>
                {editing === tn.id && (
                  <TenantEditor
                    tenant={tn}
                    busy={busy}
                    onCancel={() => setEditing(null)}
                    onSave={(body) =>
                      act(async () => {
                        await api(`/api/plataforma/tenants/${tn.id}`, { method: 'PATCH', json: body });
                        setEditing(null);
                        setNotice({ tone: 'success', text: t('plataforma.tenant_guardado') });
                      })
                    }
                  />
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface NewTenant {
  name: string;
  subdomain: string;
  organization: OrganizationFields;
  admin: { email: string; fullName: string };
}

function NewTenantForm({ busy, onSubmit, onCancel }: { busy: boolean; onSubmit: (body: NewTenant) => Promise<void>; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [subdomainEdited, setSubdomainEdited] = useState(false);
  const [org, setOrg] = useState<OrganizationFields>({ name: '', alias: null, kind: 'pais', groupName: '', timezone: 'America/Panama', countryCode: null });
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void onSubmit({
      name: name.trim(),
      subdomain,
      organization: { ...org, name: org.name.trim(), groupName: org.groupName.trim(), alias: org.alias?.trim() || null },
      admin: { email: email.trim(), fullName: fullName.trim() },
    });
  };

  return (
    <Card title={t('plataforma.nuevo_tenant')}>
      <form onSubmit={submit} className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            id="tenant-nombre"
            label={t('plataforma.tenant_nombre')}
            required
            maxLength={120}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!subdomainEdited) setSubdomain(suggestSubdomain(e.target.value));
              // El grupo de la primera organización sigue al nombre del tenant mientras no se cambie a mano.
              if (org.groupName === name.trim()) setOrg({ ...org, groupName: e.target.value.trim() });
            }}
          />
          <div className="space-y-1">
            <Field
              id="tenant-subdominio"
              label={t('plataforma.tenant_subdominio')}
              required
              pattern="[a-z0-9](?:[a-z0-9\-]{0,28}[a-z0-9])"
              value={subdomain}
              onChange={(e) => {
                setSubdomainEdited(true);
                setSubdomain(e.target.value.toLowerCase());
              }}
            />
            <p className="text-xs text-slate-500">
              {subdomain ? tenantUrl(subdomain) : ''} {t('plataforma.tenant_subdominio_ayuda')}
            </p>
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-slate-900">{t('plataforma.tenant_primera_org')}</legend>
          <OrganizationFieldset idPrefix="tenant-org" value={org} onChange={setOrg} groups={name.trim() ? [name.trim()] : []} />
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-slate-900">{t('plataforma.tenant_admin')}</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="tenant-admin-correo" type="email" label={t('plataforma.tenant_admin_correo')} required maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} />
            <Field id="tenant-admin-nombre" label={t('plataforma.tenant_admin_nombre')} required maxLength={120} value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <p className="text-xs text-slate-500">{t('plataforma.tenant_admin_ayuda')}</p>
        </fieldset>

        <div className="flex gap-2">
          <Button type="submit" disabled={busy}>
            {t('plataforma.crear_tenant')}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            {t('comun.cancelar')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function TenantEditor({
  tenant,
  busy,
  onSave,
  onCancel,
}: {
  tenant: TenantSummary;
  busy: boolean;
  onSave: (body: { name: string; storageQuotaGb: number }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(tenant.name);
  const [quota, setQuota] = useState(String(Math.round(tenant.storageQuotaBytes / GIB)));
  return (
    <form
      className="mt-4 space-y-3 border-t border-slate-100 pt-4"
      onSubmit={(e) => {
        e.preventDefault();
        void onSave({ name: name.trim(), storageQuotaGb: Number(quota) });
      }}
    >
      {tenant.isPlatform && <p className="text-xs text-slate-500">{t('plataforma.operador_fijo')}</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field id={`tenant-${tenant.id}-nombre`} label={t('plataforma.tenant_nombre')} required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
        <Field
          id={`tenant-${tenant.id}-cuota`}
          type="number"
          min={1}
          max={10000}
          label={t('plataforma.tenant_cuota')}
          required
          value={quota}
          onChange={(e) => setQuota(e.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={busy || !name.trim() || !(Number(quota) >= 1)}>
          {t('comun.guardar')}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t('comun.cancelar')}
        </Button>
      </div>
    </form>
  );
}
